import axios from "axios";
import { Config } from "../Common/Config";
import {getDeviceInfo} from "../Common/deviceInfo"
const ENDPOINT = `${Config.logs_url}trace-log/init`;

/**
 * Send a session event trace to the trace-log API.
 * @param {Object} params - Event params
 * @param {string} params.module - e.g. 'booking'
 * @param {string} params.log_name - e.g. 'BookingJourney'
 * @param {string} params.event - e.g. 'booking_flow_started'
 * @param {Object} params.payload - event payload
 * @param {string} [token] - optional Authorization token (Bearer ...) ; if omitted no Authorization header is sent
 * @returns {Promise<Object>} response data
 */
const generateSessionEventPush = async (
  { module, log_name, event, payload },
  token,
) => {
  try {
    const headers = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = token;
    let deviceData = getDeviceInfo();
    let newPayload = { ...payload, deviceInfo: deviceData };

    const body = { module, log_name, event, payload: newPayload };

    const res = await axios.post(ENDPOINT, body, { headers });

    const traceId = res?.data?.trace_log?.session_id;
    if (traceId) {
      localStorage.setItem("trace_log_id", traceId);
    } else {
      console.warn("[generateSessionEventPush] trace_log_id not returned by API — trace logging may be degraded.");
    }
    return res.data;
  } catch (err) {
    // silently fail — trace logging must NEVER crash the booking flow
    console.warn("[generateSessionEventPush] failed silently:", err?.response?.data || err.message || err);
  }
};

export default generateSessionEventPush;

/**
 * Push a detailed log event to an existing trace session.
 * Reads `trace_log_id` from localStorage if `traceLogId` is not provided.
 * @param {Object} params - Event details
 * @param {string} params.event - event name (required)
 * @param {Object} params.payload - request data
 * @param {string} params.step_status - 'success'|'failed' (required)
 * @param {Object} [params.response] - response data
 * @param {Object} [params.meta] - additional metadata
 * @param {string} [token] - optional Authorization token
 * @param {string} [traceLogId] - optional trace_log_id override
 * @returns {Promise<Object>} response data
 */
export const pushTraceLogEvent = async (
  { event, payload, step_status, response, meta },
  token,
  traceLogId,
) => {
  try {
    const id = traceLogId || localStorage.getItem("trace_log_id");
    if (!id) {
      // trace_log_id not yet set — skip silently, never crash the booking flow
      console.warn("[pushTraceLogEvent] trace_log_id not available, skipping event:", event);
      return;
    }

    const url = `${Config.logs_url}trace-log/log/${id}`;
    const headers = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = token;

    const body = { event, payload, step_status, response, meta };

    const res = await axios.post(url, body, { headers });
    return res.data;
  } catch (err) {
    // silently fail — trace logging must NEVER crash the booking flow
    console.warn("[pushTraceLogEvent] failed silently:", err?.response?.data || err.message || err);
  }
};

/**
 * Lightweight convenience wrapper around `pushTraceLogEvent` for simple click/interaction
 * logging, so every call site uses the same payload shape instead of hand-rolling one.
 * For events that need `response`/`meta`/a failed `step_status`, call `pushTraceLogEvent` directly.
 * @param {string} event - event name
 * @param {Object} [payload] - event payload
 */
export const logInteraction = (event, payload = {}) => {
  pushTraceLogEvent({ event, payload, step_status: "success" });
};

const TRACE_ENDPOINT = `${Config.logs_url}trace-log/log`;

export const pushTraceLogOnClose = async (
  { event, payload, step_status = "success", response, meta },
  token,
) => {
  try {
    const traceLogId = localStorage.getItem("trace_log_id");
    if (!traceLogId) return;

    const url = `${TRACE_ENDPOINT}/${traceLogId}`;
    const headers = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = token;
    const body = {
      event,
      payload,
      step_status,
      response,
      meta,
    };

    const blob = new Blob([body], {
      type: "application/json",
    });
    // console.log(body);
    const res = await axios.post(url, body, { headers });
    navigator.sendBeacon(url, blob);
    return res.data;
  } catch (err) {
    // silently fail — browser is closing
    console.error("Beacon log failed", err);
  }
};

/**
 * Start monitoring user activity and push an idle event if no activity for `timeout` ms.
 * Returns a `stop` function to cancel monitoring.
 *
 * Options:
 * - timeout: milliseconds before considered idle (default 15 minutes)
 * - eventName: event name to send when idle (default 'user_idle')
 * - payload: additional payload to include with the event
 * - token: optional auth token forwarded to `pushTraceLogEvent`
 * - traceLogId: optional trace id override
 * - once: if true, send idle event only once until stop is called
 */
export const startIdleMonitor = ({
  timeout = 15 * 60 * 1000,
  eventName = "user_idle",
  payload = {},
  token,
  traceLogId,
  once = true,
} = {}) => {
  let timer = null;
  let isIdle = false;

  const clearTimer = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const onIdle = async () => {
    isIdle = true;
    try {
      await pushTraceLogEvent(
        {
          event: eventName,
          payload: { ...payload, idle_ms: timeout },
          step_status: "success",
        },
        token,
        traceLogId,
      );
    } catch (err) {
      console.error("Failed to push idle event", err);
    }
    if (!once) scheduleTimer();
  };

  const scheduleTimer = () => {
    clearTimer();
    timer = setTimeout(onIdle, timeout);
  };

  const activityHandler = () => {
    if (isIdle) {
      // user returned from idle
      isIdle = false;
    }
    scheduleTimer();
  };

  const visibilityHandler = () => {
    if (document.visibilityState === "hidden") {
      // consider hidden as activity boundary — schedule timer as usual
      scheduleTimer();
    } else {
      activityHandler();
    }
  };

  // attach listeners
  const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
  events.forEach((ev) => window.addEventListener(ev, activityHandler, true));
  document.addEventListener("visibilitychange", visibilityHandler, true);

  // start
  scheduleTimer();

  // stop function
  const stop = () => {
    clearTimer();
    events.forEach((ev) =>
      window.removeEventListener(ev, activityHandler, true),
    );
    document.removeEventListener("visibilitychange", visibilityHandler, true);
  };

  return stop;
};
